import { useAuth0 } from "@auth0/auth0-react"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useRef, useState } from "react"
import { z } from "zod"

import { dispatchUpdateBatch, isOwnEvent } from "~/utils/updates/updateHandlers"
import useOrgId from "~/hooks/useOrgId"
import { useUser } from "~/providers/userProvider"
import { api } from "~/utils/api"
import { measureInteraction } from "~/utils/clientPerf"
import type { UpdateEvent } from "~/utils/updates/events"
import { syncStatusStore } from "~/stores/sync-status-store"
import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"

/**
 * Mounts the singleton subscription to backend updates (Postgres NOTIFY) and
 * routes events into the React Query cache via `updateHandlers`.
 *
 * Mount EXACTLY ONCE near the root of the authenticated tree — every additional
 * mount opens a duplicate subscription. Re-keys on org change.
 */
export function UpdatesProvider() {
  const { isAuthenticated } = useAuth0()
  const orgId = useOrgId()
  // useOrgId() falls back to localStorage and can be stale while logged out.
  if (!isAuthenticated || !orgId) return null
  return <UpdatesProviderInner key={orgId} />
}

const watermarkSchema = z.object({ id: z.string().regex(/^\d+$/) }).nullable()
type Watermark = z.infer<typeof watermarkSchema>

/** Server yields tracked(id, batch) → {id, data}; legacy/untracked yields → bare batch. */
type SubscriptionPayload = UpdateEvent[] | { id: string; data: UpdateEvent[] }

function UpdatesProviderInner() {
  const { user } = useAuth0()
  const utils = api.useUtils()
  const queryClient = useQueryClient()
  const currentUserId = user?.sub ?? null

  const [watermark, setWatermark] = useSafeLocalStorage<Watermark>(
    "syncEventWatermark",
    null,
    watermarkSchema,
  )

  // Seed once: a reactive value here would reopen the SSE stream on every batch. Org switches re-seed via key={orgId}.
  const [initialLastEventId] = useState<string | null>(() => watermark?.id ?? null)

  // Latest closures kept in refs so the subscription's onData stays
  // referentially stable across re-renders.
  const { permissions } = useUser()
  const { mutate: logInteraction } = api.clientPerf.interaction.useMutation()

  const dispatchRef = useRef<(batch: UpdateEvent[]) => void>(() => {})
  useEffect(() => {
    // The log endpoint is an edit procedure, so read-only viewers aren't measured.
    const measureRemote = permissions.canEdit
      ? () =>
          measureInteraction(
            (measurement) =>
              logInteraction({
                interaction: "remote_forecast_update",
                variantCount: utils.variantTableData.variants.getData({})?.length ?? 0,
                route: null,
                ...measurement,
              }),
            true,
          )
      : undefined
    dispatchRef.current = (batch) => {
      dispatchUpdateBatch(batch, utils, queryClient, currentUserId, measureRemote)
    }
  }, [utils, queryClient, currentUserId, permissions.canEdit, logInteraction])

  const currentUserIdRef = useRef(currentUserId)
  useEffect(() => {
    currentUserIdRef.current = currentUserId
  }, [currentUserId])

  const setWatermarkRef = useRef(setWatermark)
  useEffect(() => {
    setWatermarkRef.current = setWatermark
  }, [setWatermark])

  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => {
    return () => clearTimeout(syncTimerRef.current)
  }, [])

  const onData = useCallback(
    (payload: SubscriptionPayload) => {
      const batch = Array.isArray(payload) ? payload : payload.data
      const envelopeId = Array.isArray(payload) ? null : payload.id

      // Cursor too old to replay — reset the whole cache; envelopeId becomes the new watermark.
      if (batch.some((e) => e.eventType === "syncTooStale")) {
        void queryClient.invalidateQueries(undefined, { cancelRefetch: false })
      }
      dispatchRef.current(batch.filter((e) => e.eventType !== "syncTooStale"))

      if (envelopeId != null) {
        setWatermarkRef.current({ id: envelopeId })
      }

      // "syncing" only for edits made elsewhere — not this tab's echoes or system recomputes.
      const myId = currentUserIdRef.current
      const hasForeignEvent = batch.some((e) => e.userId != null && !isOwnEvent(e, myId))
      clearTimeout(syncTimerRef.current)
      if (hasForeignEvent) {
        syncStatusStore.getState().setStatus("syncing")
        syncTimerRef.current = setTimeout(() => {
          syncStatusStore.getState().setStatus("idle")
        }, 5_000)
      } else if (syncStatusStore.getState().status === "disconnected") {
        syncStatusStore.getState().setStatus("idle")
      }
    },
    [queryClient],
  )

  const onStarted = useCallback(() => {
    syncStatusStore.getState().setStatus("idle")
  }, [])

  const onError = useCallback((err: unknown) => {
    console.error("updates subscription error", err)
    clearTimeout(syncTimerRef.current)
    syncStatusStore.getState().setStatus("disconnected")
  }, [])

  api.updates.subscribe.useSubscription(
    { lastEventId: initialLastEventId },
    { enabled: true, onStarted, onData, onError },
  )

  return null
}
